import { Form, Formik } from "formik";
import { useMemo, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
    createSearchParams,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import { bool, object, ref, string } from "yup";
import { Button } from "../../components/Button/Button";
import userApi from "@/api/user";
import { Alert } from "@/components/Alert/Alert";
import { SSOLogin } from "@/components/SSO/SSOLogin";
import { AppText } from "@/components/Text/AppText";
import { Checkbox } from "@/components/form/Checkbox";
import { Input } from "@/components/form/Input";
import { InputError } from "@/components/form/InputError";
import {
    emailValidation,
    firstNameValidation,
    getNewPasswordSchemaField,
    lastNameValidation,
} from "@/constants/schema";
import { sendVerifyEmail } from "@/email/send";
import { getErrorMessage } from "@/helper/getErrorMessage";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useTenantFeature } from "@/hooks/useTenantFeature";
import { PasswordValidator } from "@/pages/Signup/PasswordValidator";
import { tenantNavigate } from "@/routes/utils/tenantNavigate";
import {
    getIs1TemplateTenant,
    getTenant,
    getTenantExternalLinks,
} from "@/store/tenant/selectors";
import {
    setAccessToken,
    setRefreshToken,
    setUserDetails,
} from "@/store/user/slice";
import { getUserPreferenceWizard } from "@/store/wizard/selectors";
import { RegisterRequest, User } from "@/types";
import { AppRoutes, SiteRoutes } from "@/types/routes";
import { ITenantFeature } from "@/types/tenant";
import { Tokens } from "@/types/token";
import { CLARITY_EVENT_NAMES, META_EVENT_ACTIONS } from "@/types/tracking";
import {
    Attribution,
    MarketingChannels,
    MarketingSources,
    ReferralSource,
} from "@/types/user";
import {
    trackClarityEvent,
    trackMetaEvent,
    trackPostHogEvent,
} from "@/utils/tracking";

const signUpSchema = object({
    email: emailValidation,
    password: getNewPasswordSchemaField("Password is required"),
    confirmPassword: string()
        .required("Please confirm your password")
        .oneOf([ref("password")], "Entered passwords do not match"),
    firstName: firstNameValidation,
    lastName: lastNameValidation,
    acceptedTerms: bool().oneOf(
        [true],
        "You must accept the terms and conditions",
    ),
    marketingOptIn: bool(),
});

interface SignUpFormFields {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    confirmPassword: string;
    acceptedTerms: boolean;
    marketingOptIn: boolean;
}

interface SignUpFormProps {
    onSuccess?: (user: User, tokens: Tokens) => void;
    className?: string;
}

export const SignUpForm = ({ onSuccess, className = "" }: SignUpFormProps) => {
    const initialValues: SignUpFormFields = useMemo(
        () => ({
            email: "",
            password: "",
            confirmPassword: "",
            firstName: "",
            lastName: "",
            acceptedTerms: false,
            marketingOptIn: false,
        }),
        [],
    );
    const [error, setError] = useState<string | null>("");
    const [, setLocalAccessToken] = useLocalStorage("accessToken", null);
    const [, setLocalRefreshToken] = useLocalStorage("refreshToken", null);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userPreferencesWizard = useAppSelector(getUserPreferenceWizard);
    const [loading, setLoading] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [searchParams] = useSearchParams();
    const referralCode = searchParams.get("referral_code");
    const referralSource = searchParams.get("utm_source");

    const attribution: Attribution = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            utmSource: params.get("utm_source") || undefined,
            utmMedium: params.get("utm_medium") || undefined,
            utmCampaign: params.get("utm_campaign") || undefined,
            utmContent: params.get("utm_content") || undefined,
            utmTerm: params.get("utm_term") || undefined,
            referrer: document.referrer || undefined,
        };
    }, []);
    const isPrefWizardEnabled = useTenantFeature(
        ITenantFeature.PreferencesWizard,
    );
    const tenantExternalLinks = useAppSelector(getTenantExternalLinks);
    const tenant = useAppSelector(getTenant);
    const is1TemplateTenant = useAppSelector(getIs1TemplateTenant);

    const onLoginSuccess = (user: User, tokens: Tokens) => {
        if (user.isEmailVerified) {
            dispatch(setAccessToken(tokens.access));
            dispatch(setRefreshToken(tokens.refresh));
            setLocalAccessToken(tokens.access);
            setLocalRefreshToken(tokens.refresh);
            dispatch(setUserDetails(user));
            const hasAPreferenceSet =
                (user.preferences?.jobTypes ?? []).length > 0 ||
                (user.preferences?.industries ?? []).length > 0 ||
                (user.preferences?.jobRoles ?? []).length > 0 ||
                !!user.preferences?.experienceLevel;

            tenantNavigate({
                navigate,
                isFeatureEnabled: !hasAPreferenceSet && isPrefWizardEnabled,
                enabledTo: `/${AppRoutes.Preferences}`,
                disabledTo: "/",
                navigateOptions: {
                    replace: true,
                },
            });
        }

        if (onSuccess) {
            onSuccess(user, tokens);
        }
    };

    const handleSignUp = async (values: SignUpFormFields) => {
        setError("");
        setLoading(true);

        if (!executeRecaptcha) {
            setError(
                "ReCAPTCHA not available. Please refresh the page and try again.",
            );
            setLoading(false);
            return;
        }

        try {
            const recaptchaToken = await executeRecaptcha("signup");

            const data: RegisterRequest = {
                email: values.email,
                password: values.password,
                firstName: values.firstName,
                lastName: values.lastName,
                preferences: userPreferencesWizard,
                acceptedTerms: values.acceptedTerms,
                marketingOptIns: [],
                recaptchaToken,
                signUpReferralCode: referralCode ?? undefined,
                referralSource: (referralSource as ReferralSource) ?? undefined,
                attribution,
            };

            if (values.marketingOptIn) {
                data.marketingOptIns = [
                    {
                        channel: MarketingChannels.EMAIL,
                        optedIn: true,
                        timestamp: new Date(),
                        source: MarketingSources.SIGNUP,
                    },
                ];
            }

            try {
                const res = await userApi.registerApi(data);
                const { user, tokens, verifyCode } = res;
                if (verifyCode) {
                    sendVerifyEmail(tenant, user.email, verifyCode);
                    navigate({
                        pathname: `/${AppRoutes.Verify}`,
                        search: createSearchParams({
                            email: user.email,
                        }).toString(),
                    });
                    return;
                }

                trackPostHogEvent(CLARITY_EVENT_NAMES.AUTH_SIGNUP_SUCCESS);
                trackMetaEvent(META_EVENT_ACTIONS.COMPLETED_REGISTRATION, {
                    content_name: "Signup",
                    status: true,
                });
                trackClarityEvent(CLARITY_EVENT_NAMES.AUTH_SIGNUP_SUCCESS);

                const loginRecaptchaToken = await executeRecaptcha("login");

                await userApi
                    .loginApi({
                        email: values.email,
                        password: values.password,
                        recaptchaToken: loginRecaptchaToken,
                    })
                    .then(() => {
                        onLoginSuccess(user, tokens);
                    });
            } catch (e: unknown) {
                const errorMessage =
                    typeof e === "object" && e !== null && "message" in e
                        ? (e.message as string)
                        : e?.toString() || "Unknown error occurred";
                if (errorMessage.includes("Email already taken")) {
                    setError("An account with this email already exists");
                } else {
                    throw e; // Re-throw other errors to be caught by outer catch block
                }
                setLoading(false);
                return;
            }
        } catch (e) {
            setError(getErrorMessage(e, "Register"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={className}>
            {is1TemplateTenant && (
                <div className="mt-6">
                    <SSOLogin
                        setError={setError}
                        onSuccess={onLoginSuccess}
                    />
                    <div className="my-4 flex items-center">
                        <div className="h-px flex-1 bg-neutral-200"></div>
                        <AppText
                            variant="footnote"
                            className="mx-4 !font-medium text-neutral-500"
                        >
                            or continue with email
                        </AppText>
                        <div className="h-px flex-1 bg-neutral-200"></div>
                    </div>
                </div>
            )}
            <Formik
                initialValues={initialValues}
                onSubmit={handleSignUp}
                validationSchema={signUpSchema}
                validateOnBlur={false}
                validateOnChange={false}
                validateOnMount={false}
            >
                {({ values, errors, handleChange }) => (
                    <Form className="mt-2">
                        <div className="mx-auto max-w-[450px] lg:mx-0">
                            {error && (
                                <Alert
                                    type="error"
                                    message={error}
                                    className="mb-4"
                                />
                            )}

                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-4">
                                    <Input
                                        type="text"
                                        autoComplete="firstName"
                                        label="First Name"
                                        name="firstName"
                                        formikChange={handleChange}
                                        error={errors.firstName}
                                        value={values.firstName}
                                        containerClassName="col-span-1"
                                    />
                                    <Input
                                        type="text"
                                        autoComplete="lastName"
                                        label="Last Name"
                                        name="lastName"
                                        formikChange={handleChange}
                                        error={errors.lastName}
                                        value={values.lastName}
                                        containerClassName="col-span-1"
                                    />
                                </div>

                                <Input
                                    type="text"
                                    autoComplete="email"
                                    label="Email"
                                    name="email"
                                    formikChange={handleChange}
                                    error={errors.email}
                                    value={values.email}
                                />

                                <Input
                                    type="password"
                                    autoComplete="password"
                                    label="Password"
                                    name="password"
                                    formikChange={handleChange}
                                    error={errors.password}
                                    value={values.password}
                                />

                                <PasswordValidator password={values.password} />

                                <Input
                                    type="password"
                                    label="Confirm password"
                                    name="confirmPassword"
                                    formikChange={handleChange}
                                    error={errors.confirmPassword}
                                    value={values.confirmPassword}
                                />
                            </div>

                            <div className="mt-4 rounded-lg bg-neutral-50">
                                <Checkbox
                                    label={
                                        <AppText variant="labelsbuttons">
                                            I agree to{" "}
                                            <a
                                                href={
                                                    tenantExternalLinks?.termsOfService
                                                        ? tenantExternalLinks.termsOfService
                                                        : SiteRoutes.Terms
                                                }
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-primary-700"
                                            >
                                                Terms of Service
                                            </a>{" "}
                                            and{" "}
                                            <a
                                                href={
                                                    tenantExternalLinks?.privacyPolicy
                                                        ? tenantExternalLinks.privacyPolicy
                                                        : SiteRoutes.PrivacyPolicy
                                                }
                                                target="blank"
                                                rel="noreferrer"
                                                className="text-primary-700"
                                            >
                                                Privacy Policy
                                            </a>
                                        </AppText>
                                    }
                                    id={"acceptedTerms"}
                                    borderless
                                    className="px-0 py-1"
                                    checked={values.acceptedTerms}
                                    onChange={checked => {
                                        handleChange({
                                            target: {
                                                name: "acceptedTerms",
                                                value: checked,
                                            },
                                        });
                                    }}
                                />
                                <div className="pl-9">
                                    <InputError error={errors.acceptedTerms} />
                                </div>
                                {is1TemplateTenant && (
                                    <Checkbox
                                        label="Sign me up for emails and updates from 1Template"
                                        id={"marketingOptIn"}
                                        borderless
                                        className="px-0 py-1"
                                        checked={values.marketingOptIn}
                                        onChange={checked => {
                                            handleChange({
                                                target: {
                                                    name: "marketingOptIn",
                                                    value: checked,
                                                },
                                            });
                                        }}
                                    />
                                )}
                            </div>

                            <Button
                                className="mt-4 w-full"
                                type="submit"
                                disabled={loading}
                                loading={loading}
                            >
                                Create Account
                            </Button>

                            <div className="mt-3 text-center">
                                <Button
                                    to={`/${AppRoutes.Login}`}
                                    color={"primary"}
                                    variant={"text"}
                                    className="text-sm"
                                >
                                    Already have an account? Sign in
                                </Button>
                            </div>
                            <div className="mt-2 flex justify-center">
                                <AppText
                                    variant="footnote"
                                    className="w-[300px] text-center text-neutral-700 md:w-[340px]"
                                >
                                    By continuing, you agree to{" "}
                                    {tenant?.name
                                        ? `${tenant.name}'s `
                                        : "the "}
                                    <a
                                        href={
                                            tenantExternalLinks?.termsOfService
                                                ? tenantExternalLinks.termsOfService
                                                : SiteRoutes.Terms
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary-700"
                                    >
                                        Terms of Service
                                    </a>
                                    . Read our{" "}
                                    <a
                                        href={
                                            tenantExternalLinks?.privacyPolicy
                                                ? tenantExternalLinks.privacyPolicy
                                                : SiteRoutes.PrivacyPolicy
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary-700"
                                    >
                                        Privacy Policy
                                    </a>
                                    .
                                </AppText>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};
